import React, { useCallback, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  Alert,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  Chip,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
} from "@mui/material";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import DashboardCustomizeOutlined from "@mui/icons-material/DashboardCustomizeOutlined";
import DataObjectOutlined from "@mui/icons-material/DataObjectOutlined";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import EmptyStateWithAI from "../EmptyStateWithAI";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import ChatbotFlowImportHelpDialog from "./ChatbotFlowImportHelpDialog";
import ChatbotFlowImportWizard from "./ChatbotFlowImportWizard";
import ChatbotFlowImportProgress from "./ChatbotFlowImportProgress";
import { fetchData, importChatbotFlowFromPdf, getChatbotFlowImportJob } from "../../api/client";
import { uploadOne } from "../../hooks/useS3Upload";
import { extractPdfTextFromFile } from "../../utils/extractPdfTextClient";
import { getUserFacingApiMessage } from "../../utils/getUserFacingApiMessage";
import {
  parseFlowDocument,
  parseExcelFlowFile,
  readFileAsText,
  readFileAsArrayBuffer,
  readFileAsBase64,
} from "../../utils/chatbotFlowExchange";

const PDF_INLINE_MAX_BYTES = 6 * 1024 * 1024;
const PDF_IMPORT_ENQUEUE_TIMEOUT_MS = 60000;
const PDF_IMPORT_POLL_INTERVAL_MS = 2500;
const PDF_IMPORT_POLL_MAX_MS = 1200000;
const SLOTS_REFETCH_INTERVAL_MS = 1500;
const SLOTS_REFETCH_MAX_MS = 120000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchChatbotSlots(userId) {
  const params = new URLSearchParams({ user_id: String(userId) });
  const res = await fetchData(`/chatbot/slots?${params.toString()}`, "GET");
  const data = res?.data ?? res ?? [];
  return Array.isArray(data) ? data : [];
}

async function waitForImportedSlots(queryClient, userId) {
  const started = Date.now();
  while (Date.now() - started < SLOTS_REFETCH_MAX_MS) {
    await queryClient.invalidateQueries(["chatbot", "slots", userId]);
    await queryClient.invalidateQueries(["chatbot"]);
    const slots = await fetchChatbotSlots(userId);
    queryClient.setQueryData(["chatbot", "slots", userId, null], slots);
    if (slots.length > 0) return slots;
    await sleep(SLOTS_REFETCH_INTERVAL_MS);
  }
  const slots = await fetchChatbotSlots(userId);
  queryClient.setQueryData(["chatbot", "slots", userId, null], slots);
  return slots;
}

async function pollChatbotFlowImportJob(jobId, { onStatus, t }) {
  const started = Date.now();
  while (Date.now() - started < PDF_IMPORT_POLL_MAX_MS) {
    const job = await getChatbotFlowImportJob(jobId);
    const status = String(job?.status || "").toLowerCase();
    onStatus?.(status, job);
    if (status === "completed") return job;
    if (status === "failed" || status === "cancelled") {
      const err = new Error(job?.errorMessage || t("chatbotConfigPage.flowImportPdfError"));
      err.job = job;
      throw err;
    }
    await sleep(PDF_IMPORT_POLL_INTERVAL_MS);
  }
  const err = new Error(t("chatbotConfigPage.flowImportPdfTimeout"));
  err.code = "poll_timeout";
  throw err;
}

export default function ChatbotCreatePanel({ canCreate, userData, onError, onSnack }) {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [creatingBot, setCreatingBot] = useState(false);
  const [creatingWithAI, setCreatingWithAI] = useState(false);
  const [creatingFromTemplate, setCreatingFromTemplate] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [newBotName, setNewBotName] = useState("");
  const [openCreateBot, setOpenCreateBot] = useState(false);
  const [openAiDialog, setOpenAiDialog] = useState(false);
  const [aiInstructions, setAiInstructions] = useState("");
  const [importHelpType, setImportHelpType] = useState(null);
  const [importMenuAnchor, setImportMenuAnchor] = useState(null);
  const [openNameForImport, setOpenNameForImport] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [pendingImportType, setPendingImportType] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardFlowDoc, setWizardFlowDoc] = useState(null);
  const [wizardSourceLabel, setWizardSourceLabel] = useState("JSON");
  const [confirmPdfOpen, setConfirmPdfOpen] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateBotName, setTemplateBotName] = useState("");

  const jsonInputRef = useRef(null);
  const excelInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const patchProgress = (patch) => {
    setImportProgress((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const createBotUser = useCallback(
    async (name) => {
      const created = await fetchData("/users", "POST", {
        nome: name.trim(),
        status: 1,
        ura: 1,
        incluir_nome: 0,
        id_usuario: userData?.id ?? null,
        id_usuario_ultima_alteracao: userData?.id ?? null,
      });
      return created?.data?.id ?? created?.id;
    },
    [userData?.id]
  );

  const finishCreate = useCallback(
    (createdUserId) => {
      queryClient.invalidateQueries(["chatbots"]);
      queryClient.invalidateQueries(["chatbot"]);
      if (createdUserId) {
        navigate(`/chatbots/${createdUserId}`, { replace: true });
      } else {
        navigate("/chatbots", { replace: true });
      }
    },
    [navigate, queryClient]
  );

  const handleCreateBotUser = async () => {
    if (!canCreate) {
      onSnack?.({ severity: "warning", message: t("chatbotFormPage.noPermissionCreate") });
      return;
    }
    if (!newBotName.trim()) {
      onError?.(t("chatbotConfigPage.nameRequired"));
      return;
    }
    setCreatingBot(true);
    try {
      onError?.(null);
      const createdUserId = await createBotUser(newBotName);
      setOpenCreateBot(false);
      setNewBotName("");
      finishCreate(createdUserId);
    } catch (err) {
      onError?.(err?.message || t("chatbotConfigPage.errorCreateUser"));
    } finally {
      setCreatingBot(false);
    }
  };

  const handleCreateBotUserWithAI = async () => {
    if (!canCreate) {
      onSnack?.({ severity: "warning", message: t("chatbotFormPage.noPermissionCreate") });
      return;
    }
    setCreatingWithAI(true);
    try {
      onError?.(null);
      const name = newBotName.trim() || `ChatBot ${new Date().toLocaleDateString("pt-BR")}`;
      const createdUserId = await createBotUser(name);
      if (createdUserId) {
        await fetchData("/chatbot/slots/generate-initial", "POST", {
          user_id: createdUserId,
          user_instructions: aiInstructions.trim() || undefined,
        });
        queryClient.invalidateQueries(["chatbot", "slots", createdUserId]);
      }
      setOpenAiDialog(false);
      setAiInstructions("");
      setNewBotName("");
      finishCreate(createdUserId);
    } catch (err) {
      onError?.(err?.message || t("chatbotConfigPage.errorCreateAI"));
    } finally {
      setCreatingWithAI(false);
    }
  };

  const runImportOnUser = async (userId, flowDoc, sourceLabel) => {
    await fetchData("/chatbot/slots/import", "POST", {
      user_id: userId,
      flow: flowDoc,
      replace: true,
    });
    await queryClient.invalidateQueries(["chatbot", "slots", userId]);
    await queryClient.invalidateQueries(["chatbot"]);
    onSnack?.({
      severity: "success",
      message: t("chatbotConfigPage.flowImportSuccess", { source: sourceLabel }),
    });
  };

  const runPdfImportOnUser = async (userId, file) => {
    setImportProgress({
      open: true,
      phase: "extracting",
      fileName: file.name,
      logs: [],
      page: 0,
      totalPages: 0,
    });
    try {
      const payload = { user_id: userId, filename: file.name, replace: true };
      let pdfText = null;
      try {
        pdfText = await extractPdfTextFromFile(file, {
          onProgress: (page, total) => {
            patchProgress({ phase: "extracting", page, totalPages: total });
          },
        });
      } catch {
        pdfText = null;
      }
      if (pdfText) {
        payload.pdf_text = pdfText;
      } else if (file.size > PDF_INLINE_MAX_BYTES) {
        patchProgress({ phase: "uploading" });
        const { key } = await uploadOne(file, { prefix: "chatbot-flow-imports" });
        payload.s3_key = key;
      } else {
        payload.file_base64 = await readFileAsBase64(file);
      }
      patchProgress({ phase: "sending", logs: [] });
      const enqueue = await importChatbotFlowFromPdf(payload, { timeout: PDF_IMPORT_ENQUEUE_TIMEOUT_MS });
      if (enqueue?.accepted && enqueue?.jobId) {
        patchProgress({
          phase: "queued",
          jobId: enqueue.jobId,
          serverStatus: "queued",
          logs: [t("chatbotConfigPage.flowImportPdfQueued")],
        });
        await pollChatbotFlowImportJob(enqueue.jobId, {
          t,
          onStatus: (status, job) => {
            const logs = Array.isArray(job?.log) ? job.log : [];
            patchProgress({
              phase: status === "queued" ? "queued" : "running",
              serverStatus: status,
              logs: logs.length ? logs : undefined,
            });
          },
        });
      } else {
        patchProgress({ phase: "sync" });
      }
      patchProgress({ phase: "saving", serverStatus: undefined });
      await waitForImportedSlots(queryClient, userId);
      patchProgress({ phase: "completed" });
    } catch (err) {
      const { message } = getUserFacingApiMessage(err);
      patchProgress({ phase: "failed", error: message || err?.message });
      throw err;
    }
  };

  const proceedAfterNameForImport = async () => {
    if (!newBotName.trim()) {
      onError?.(t("chatbotConfigPage.nameRequired"));
      return;
    }
    setOpenNameForImport(false);
    const file = pendingImportFile;
    const type = pendingImportType;
    setPendingImportFile(null);
    setPendingImportType(null);

    if (type === "pdf") {
      setConfirmPdfOpen(true);
      setPendingImportFile(file);
      return;
    }

    try {
      let flowDoc;
      if (type === "json") {
        const text = await readFileAsText(file);
        flowDoc = parseFlowDocument(text);
        setWizardSourceLabel("JSON");
      } else {
        const buffer = await readFileAsArrayBuffer(file);
        flowDoc = parseExcelFlowFile(buffer, newBotName.trim());
        parseFlowDocument(flowDoc);
        setWizardSourceLabel("Excel");
      }
      setWizardFlowDoc(flowDoc);
      setWizardOpen(true);
    } catch {
      onError?.(
        type === "json"
          ? t("chatbotConfigPage.flowImportInvalidJson")
          : t("chatbotConfigPage.flowImportInvalidExcel")
      );
    }
  };

  const handleConfirmPdfImport = async () => {
    if (!canCreate || !pendingImportFile) return;
    setConfirmPdfOpen(false);
    setImportBusy(true);
    try {
      onError?.(null);
      const createdUserId = await createBotUser(newBotName.trim());
      await runPdfImportOnUser(createdUserId, pendingImportFile);
      setNewBotName("");
      setPendingImportFile(null);
      finishCreate(createdUserId);
    } catch (err) {
      onError?.(err?.message || t("chatbotConfigPage.flowImportPdfError"));
    } finally {
      setImportBusy(false);
    }
  };

  const handleImportFile = async (type, file) => {
    if (!file || !canCreate) return;
    setPendingImportFile(file);
    setPendingImportType(type);
    setOpenNameForImport(true);
  };

  const openFilePickerForImportType = (type) => {
    if (type === "json") jsonInputRef.current?.click();
    else if (type === "excel") excelInputRef.current?.click();
    else if (type === "pdf") pdfInputRef.current?.click();
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await fetchData("/chatbot/flow-templates", "GET");
      const items = res?.data?.items ?? res?.items ?? [];
      setTemplates(Array.isArray(items) ? items : []);
    } catch (err) {
      onError?.(err?.message || t("chatbotConfigPage.templateLoadError"));
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const openTemplateDialog = () => {
    setTemplateDialogOpen(true);
    setSelectedTemplateId(null);
    setTemplateBotName("");
    loadTemplates();
  };

  const handleCreateFromTemplate = async () => {
    if (!canCreate || !selectedTemplateId) return;
    if (!templateBotName.trim()) {
      onError?.(t("chatbotConfigPage.nameRequired"));
      return;
    }
    setCreatingFromTemplate(true);
    try {
      onError?.(null);
      const tplRes = await fetchData(`/chatbot/flow-templates/${selectedTemplateId}`, "GET");
      const tpl = tplRes?.data ?? tplRes;
      const flow = tpl?.flow;
      if (!flow) throw new Error(t("chatbotConfigPage.templateFlowMissing"));
      parseFlowDocument(flow);
      setNewBotName(templateBotName.trim());
      setWizardFlowDoc(flow);
      setWizardSourceLabel(t("chatbotConfigPage.createFromTemplate"));
      setTemplateDialogOpen(false);
      setSelectedTemplateId(null);
      setTemplateBotName("");
      setWizardOpen(true);
    } catch (err) {
      onError?.(err?.message || t("chatbotConfigPage.templateCreateError"));
    } finally {
      setCreatingFromTemplate(false);
    }
  };

  return (
    <>
      <EmptyStateWithAI
        title={t("chatbotConfigPage.emptyTitle")}
        description={t("chatbotConfigPage.emptyDescriptionExtended")}
        primaryLabel={t("chatbotConfigPage.createWithAI")}
        secondaryLabel={t("chatbotConfigPage.createManually")}
        onPrimaryClick={() => {
          setNewBotName("");
          setAiInstructions("");
          setOpenAiDialog(true);
        }}
        onSecondaryClick={() => setOpenCreateBot(true)}
        primaryLoading={creatingWithAI}
        icon={<SmartToyOutlined sx={{ fontSize: 56 }} color="primary" />}
        maxWidth="920px"
        extraActions={[
          {
            label: t("chatbotConfigPage.createFromImport"),
            icon: <FileUploadOutlined />,
            onClick: (e) => setImportMenuAnchor(e.currentTarget),
            disabled: importBusy,
            loading: importBusy,
          },
          {
            label: t("chatbotConfigPage.createFromTemplate"),
            icon: <DashboardCustomizeOutlined />,
            onClick: openTemplateDialog,
            disabled: creatingFromTemplate,
            loading: creatingFromTemplate,
          },
        ]}
      />

      <Dialog open={openCreateBot} onClose={() => !creatingBot && setOpenCreateBot(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t("chatbotConfigPage.newChatbot")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label={t("chatbotConfigPage.chatbotName")}
            fullWidth
            value={newBotName}
            onChange={(e) => setNewBotName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateBot(false)} disabled={creatingBot}>
            {t("shared.cancel")}
          </Button>
          <Button onClick={handleCreateBotUser} disabled={creatingBot} variant="contained">
            {creatingBot ? <CircularProgress size={20} /> : t("chatbotConfigPage.create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAiDialog} onClose={() => !creatingWithAI && setOpenAiDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("chatbotConfigPage.createWithAITitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t("chatbotConfigPage.createWithAIHint")}
            </Typography>
            <TextField
              label={t("chatbotConfigPage.chatbotName")}
              fullWidth
              value={newBotName}
              onChange={(e) => setNewBotName(e.target.value)}
              placeholder={`ChatBot ${new Date().toLocaleDateString("pt-BR")}`}
            />
            <TextField
              label={t("chatbotConfigPage.createWithAIInstructions")}
              fullWidth
              multiline
              minRows={4}
              value={aiInstructions}
              onChange={(e) => setAiInstructions(e.target.value)}
              placeholder={t("chatbotConfigPage.createWithAIInstructionsPlaceholder")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAiDialog(false)} disabled={creatingWithAI}>
            {t("shared.cancel")}
          </Button>
          <Button onClick={handleCreateBotUserWithAI} disabled={creatingWithAI} variant="contained">
            {creatingWithAI ? <CircularProgress size={20} /> : t("chatbotConfigPage.createWithAI")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openNameForImport} onClose={() => setOpenNameForImport(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t("chatbotConfigPage.createFromImportTitle")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label={t("chatbotConfigPage.chatbotName")}
            fullWidth
            value={newBotName}
            onChange={(e) => setNewBotName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNameForImport(false)}>{t("shared.cancel")}</Button>
          <Button onClick={proceedAfterNameForImport} variant="contained">
            {t("shared.continue")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmPdfOpen} onClose={importBusy ? undefined : () => setConfirmPdfOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("chatbotConfigPage.flowImportConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("chatbotConfigPage.flowImportConfirmBody", { source: "PDF" })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPdfOpen(false)} disabled={importBusy}>
            {t("shared.cancel")}
          </Button>
          <Button onClick={handleConfirmPdfImport} variant="contained" disabled={importBusy} autoFocus>
            {importBusy ? <CircularProgress size={20} /> : t("chatbotConfigPage.flowImportConfirmAction")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={templateDialogOpen} onClose={() => !creatingFromTemplate && setTemplateDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t("chatbotConfigPage.createFromTemplateTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={t("chatbotConfigPage.chatbotName")}
              fullWidth
              value={templateBotName}
              onChange={(e) => setTemplateBotName(e.target.value)}
            />
            {templatesLoading ? (
              <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={28} />
              </Box>
            ) : templates.length === 0 ? (
              <Alert severity="info">{t("chatbotConfigPage.templateEmpty")}</Alert>
            ) : (
              <List dense disablePadding>
                {templates.map((tpl) => (
                  <ListItemButton
                    key={tpl.id}
                    selected={Number(selectedTemplateId) === Number(tpl.id)}
                    onClick={() => setSelectedTemplateId(tpl.id)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={tpl.title}
                      secondary={
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          {tpl.category ? <Chip size="small" label={tpl.category} /> : null}
                          {tpl.description ? (
                            <Typography variant="caption" color="text.secondary">
                              {tpl.description}
                            </Typography>
                          ) : null}
                        </Stack>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)} disabled={creatingFromTemplate}>
            {t("shared.cancel")}
          </Button>
          <Button
            onClick={handleCreateFromTemplate}
            variant="contained"
            disabled={creatingFromTemplate || !selectedTemplateId || !templateBotName.trim()}
          >
            {creatingFromTemplate ? <CircularProgress size={20} /> : t("chatbotConfigPage.createFromTemplateAction")}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu anchorEl={importMenuAnchor} open={!!importMenuAnchor} onClose={() => setImportMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setImportMenuAnchor(null);
            setImportHelpType("json");
          }}
        >
          <ListItemIcon>
            <DataObjectOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("chatbotConfigPage.flowImportJson")} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setImportMenuAnchor(null);
            setImportHelpType("excel");
          }}
        >
          <ListItemIcon>
            <TableChartOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("chatbotConfigPage.flowImportExcel")} />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setImportMenuAnchor(null);
            setImportHelpType("pdf");
          }}
        >
          <ListItemIcon>
            <PictureAsPdfOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("chatbotConfigPage.flowImportPdf")}
            secondary={t("chatbotConfigPage.flowImportPdfHint")}
          />
        </MenuItem>
      </Menu>

      <ChatbotFlowImportHelpDialog
        type={importHelpType}
        open={Boolean(importHelpType)}
        onClose={() => setImportHelpType(null)}
        onSelectFile={(type) => {
          setImportHelpType(null);
          openFilePickerForImportType(type);
        }}
      />

      <input
        ref={jsonInputRef}
        type="file"
        accept=".json,application/json"
        hidden
        onChange={(e) => {
          handleImportFile("json", e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <input
        ref={excelInputRef}
        type="file"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.csv,text/csv"
        hidden
        onChange={(e) => {
          handleImportFile("excel", e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <input
        ref={pdfInputRef}
        type="file"
        accept=".pdf,application/pdf"
        hidden
        onChange={(e) => {
          handleImportFile("pdf", e.target.files?.[0]);
          e.target.value = "";
        }}
      />

      <ChatbotFlowImportProgress progress={importProgress} onClose={() => setImportProgress(null)} />

      <ChatbotFlowImportWizard
        open={wizardOpen}
        flowDoc={wizardFlowDoc}
        parameterForm={null}
        sourceLabel={wizardSourceLabel}
        onClose={() => {
          if (importBusy) return;
          setWizardOpen(false);
          setWizardFlowDoc(null);
        }}
        onConfirm={async (mappedFlow) => {
          setWizardOpen(false);
          setImportBusy(true);
          try {
            onError?.(null);
            const createdUserId = await createBotUser(newBotName.trim());
            await runImportOnUser(createdUserId, mappedFlow, wizardSourceLabel);
            setNewBotName("");
            setWizardFlowDoc(null);
            finishCreate(createdUserId);
          } catch (err) {
            onError?.(err?.message || t("chatbotConfigPage.flowImportError"));
          } finally {
            setImportBusy(false);
          }
        }}
      />
    </>
  );
}
